/* eslint-disable */
// src/pages/Leads.js — Fully Redesigned Leads Table with Cell Popovers & Undo
import React, {
  useEffect, useMemo, useRef, useState, useCallback,
} from "react";
import { Form, Button, Modal, Badge } from "react-bootstrap";
import Papa from "papaparse";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { LEAD_STATUSES, LEAD_SOURCES } from "../utils/constants";
import { canEditLead } from "../utils/permissions";
import { fmtDate, timeAgo } from "../utils/formatters";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Upload, Plus, Copy, Phone, Star,
  Search, Keyboard, X, ChevronRight, MessageSquare,
  Maximize2, Minimize2, Columns, ArrowUp, ArrowDown, RotateCcw, Bell,
  RefreshCw, Globe, FileSpreadsheet, Download, AlertCircle, CheckCircle2
} from "lucide-react";

import StatusBadge, { STATUS_CONFIG } from "../components/StatusBadge";
import { StatusPopover, AgentPopover, BrandPopover } from "../components/LeadPopovers";
import FloatingBulkBar from "../components/FloatingBulkBar";
import LeadHelpModal from "../components/LeadHelpModal";
import BulkWhatsAppModal from "../components/BulkWhatsAppModal";
import AIIntentBadge from "../components/ai/AIIntentBadge";
import SetReminderModal from "../components/SetReminderModal";
import "./leads.css";

function copyToClipboard(text, label = 'Copied to clipboard!') {
  if (!text) return;
  navigator.clipboard?.writeText(text).then(() => toast.success(label, { duration: 1500 }));
}

// Calculate staleness status dot color based on last contact date
function getStalenessDot(lastContactDate) {
  if (!lastContactDate) return 'red';
  const now = new Date();
  const diffDays = (now - new Date(lastContactDate)) / (1000 * 60 * 60 * 24);
  if (diffDays <= 3) return 'green';
  if (diffDays <= 7) return 'amber';
  return 'red';
}

const DEFAULT_COLUMN_CONFIG = [
  { id: 'aiIntent',    label: 'AI Intent',    visible: true,  minWidth: '100px' },
  { id: 'phone',       label: 'Phone',        visible: true,  minWidth: '140px' },
  { id: 'email',       label: 'Email',        visible: true,  minWidth: '170px' },
  { id: 'lastContact', label: 'Last Contact', visible: true,  minWidth: '120px' },
  { id: 'source',      label: 'Source',       visible: true,  minWidth: '120px' },
  { id: 'status',      label: 'Status',       visible: true,  minWidth: '140px' },
  { id: 'brand',       label: 'Brand',        visible: true,  minWidth: '120px' },
  { id: 'agent',       label: 'Agent',        visible: true,  minWidth: '140px' },
  { id: 'remarks',     label: 'Remarks',      visible: true,  minWidth: '200px' },
  { id: 'lastEdit',    label: 'Last Edit',    visible: false, minWidth: '110px' },
];

const getInitialColumns = () => {
  try {
    const saved = localStorage.getItem('crm_leads_columns_v1');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const validIds = new Set(parsed.map(c => c.id));
        const merged = parsed.filter(c => DEFAULT_COLUMN_CONFIG.some(dc => dc.id === c.id));
        DEFAULT_COLUMN_CONFIG.forEach(dc => {
          if (!validIds.has(dc.id)) merged.push(dc);
        });
        return merged;
      }
    }
  } catch (_) {}
  return DEFAULT_COLUMN_CONFIG;
};

const Leads = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [leads, setLeads]         = useState([]);
  const [employees, setEmployees] = useState([]);
  const [brands, setBrands]       = useState([]);
  const [loading, setLoading]     = useState(true);

  // Modals & UI toggles
  const [showAddModal, setShowAddModal]   = useState(false);
  const [showCsvModal, setShowCsvModal]   = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showWaModal, setShowWaModal]     = useState(false);
  const [csvRows, setCsvRows]             = useState([]);
  const [csvError, setCsvError]           = useState('');
  const [csvBusy, setCsvBusy]             = useState(false);

  // Advanced CSV & Leads Import State
  const [csvTab, setCsvTab]                     = useState('file'); // 'file' | 'paste'
  const [csvFileName, setCsvFileName]           = useState('');
  const [csvPasteText, setCsvPasteText]         = useState('');
  const [csvTargetAgent, setCsvTargetAgent]     = useState('');
  const [csvDefaultSource, setCsvDefaultSource] = useState('Facebook Ads');
  const [csvDefaultBrand, setCsvDefaultBrand]   = useState('1');
  const [csvRawData, setCsvRawData]             = useState([]);
  const [csvHeaders, setCsvHeaders]             = useState([]);
  const [csvMapping, setCsvMapping]             = useState({ name: '', phone: '', email: '', remarks: '', source: '', agent: '', brand: '' });
  const [skipDuplicates, setSkipDuplicates]     = useState(true);
  const [isDragging, setIsDragging]             = useState(false);
  const fileInputRef                            = useRef(null);

  const sanitizePhoneClient = (raw) => {
    if (!raw) return "";
    let clean = String(raw).trim().replace(/[^\d+]/g, '');
    if (clean.startsWith('+')) {
      clean = '+' + clean.slice(1).replace(/\+/g, '');
    } else {
      clean = clean.replace(/\+/g, '');
    }
    return clean;
  };

  const guessColumn = (headers, candidates) => {
    if (!headers || !headers.length) return '';
    const cleanCandidates = candidates.map(c => c.toLowerCase().replace(/[^a-z0-9]/g, ''));
    // Exact match first
    const exact = headers.find(h => {
      const cleanH = h.toLowerCase().replace(/[^a-z0-9]/g, '');
      return cleanCandidates.includes(cleanH);
    });
    if (exact) return exact;

    // Substring match
    return headers.find(h => {
      const cleanH = h.toLowerCase().replace(/[^a-z0-9]/g, '');
      return cleanCandidates.some(c => cleanH.includes(c) || (cleanH.length >= 4 && c.includes(cleanH)));
    }) || '';
  };

  const resolveAgent = (rawVal, empList) => {
    if (!rawVal || !Array.isArray(empList) || empList.length === 0) return null;
    const val = String(rawVal).trim();
    if (!val) return null;

    // 1. Numeric ID
    const num = Number(val);
    if (Number.isInteger(num) && num > 0) {
      const match = empList.find(e => Number(e.id || e._id) === num);
      if (match) return { id: Number(match.id || match._id), name: match.name, email: match.email };
    }

    const lowerVal = val.toLowerCase();

    // 2. Exact email
    const byEmail = empList.find(e => (e.email || '').trim().toLowerCase() === lowerVal);
    if (byEmail) return { id: Number(byEmail.id || byEmail._id), name: byEmail.name, email: byEmail.email };

    // 3. Exact full name
    const byName = empList.find(e => (e.name || '').trim().toLowerCase() === lowerVal);
    if (byName) return { id: Number(byName.id || byName._id), name: byName.name, email: byName.email };

    // 4. Normalized name (letters & digits only)
    const normVal = lowerVal.replace(/[^a-z0-9]/g, '');
    if (normVal) {
      const byNorm = empList.find(e => (e.name || '').toLowerCase().replace(/[^a-z0-9]/g, '') === normVal);
      if (byNorm) return { id: Number(byNorm.id || byNorm._id), name: byNorm.name, email: byNorm.email };
    }

    // 5. Unambiguous partial or first name
    const partials = empList.filter(e => {
      const eName = (e.name || '').trim().toLowerCase();
      return eName.includes(lowerVal) || (normVal.length >= 3 && eName.replace(/[^a-z0-9]/g, '').includes(normVal));
    });
    if (partials.length === 1) {
      return { id: Number(partials[0].id || partials[0]._id), name: partials[0].name, email: partials[0].email };
    }

    return { id: null, raw: val, unmatched: true };
  };

  const resolveBrand = (rawVal, brandList) => {
    if (!rawVal || !Array.isArray(brandList) || brandList.length === 0) return null;
    const val = String(rawVal).trim();
    if (!val) return null;

    const num = Number(val);
    if (Number.isInteger(num) && num > 0) {
      const match = brandList.find(b => Number(b.id || b._id) === num);
      if (match) return { id: Number(match.id || match._id), name: match.name, color: match.color };
    }

    const lowerVal = val.toLowerCase();
    const byExact = brandList.find(b => (b.name || '').trim().toLowerCase() === lowerVal);
    if (byExact) return { id: Number(byExact.id || byExact._id), name: byExact.name, color: byExact.color };

    const bySlug = brandList.find(b => (b.slug || '').trim().toLowerCase() === lowerVal);
    if (bySlug) return { id: Number(bySlug.id || bySlug._id), name: bySlug.name, color: bySlug.color };

    if (lowerVal.includes('platinum')) {
      const b = brandList.find(x => (x.name || '').toLowerCase().includes('platinum'));
      if (b) return { id: Number(b.id || b._id), name: b.name, color: b.color };
    }
    if (lowerVal.includes('excellence')) {
      const b = brandList.find(x => (x.name || '').toLowerCase().includes('excellence'));
      if (b) return { id: Number(b.id || b._id), name: b.name, color: b.color };
    }
    if (lowerVal.includes('afj')) {
      const b = brandList.find(x => (x.name || '').toLowerCase().includes('afj'));
      if (b) return { id: Number(b.id || b._id), name: b.name, color: b.color };
    }

    return null;
  };

  const parseCsvContent = (contentString) => {
    setCsvError('');
    Papa.parse(contentString, {
      header: true,
      skipEmptyLines: 'greedy',
      dynamicTyping: false,
      transformHeader: h => h.trim(),
      complete: (results) => {
        if (!results.data || results.data.length === 0) {
          setCsvError('No rows found in the CSV data');
          setCsvRawData([]);
          setCsvHeaders([]);
          return;
        }
        const headers = results.meta?.fields || Object.keys(results.data[0] || {});
        setCsvHeaders(headers);
        setCsvRawData(results.data);

        // Auto guess mapping
        const nameCol = guessColumn(headers, ['name', 'full name', 'fullname', 'lead name', 'lead_name', 'customer name', 'client name', 'first name', 'contact name']);
        const phoneCol = guessColumn(headers, ['phone', 'phone number', 'phone_number', 'phonenumber', 'number', 'mobile', 'mobile number', 'cell', 'tel', 'contact', 'whatsapp', 'wa', 'raw_phone_number']);
        const emailCol = guessColumn(headers, ['email', 'email address', 'email_address', 'e-mail', 'mail']);
        const remarksCol = guessColumn(headers, ['remarks', 'notes', 'comments', 'inquiry', 'message', 'car', 'vehicle', 'requirements', 'details']);
        const sourceCol = guessColumn(headers, ['source', 'lead source', 'campaign', 'ad name', 'platform', 'channel']);
        const agentCol = guessColumn(headers, ['assigned agent', 'assigned person', 'assigned to', 'assigned_to', 'assignedagent', 'agent name', 'agent_name', 'agent', 'sales rep', 'sales agent', 'rep', 'assignee', 'owner']);
        const brandCol = guessColumn(headers, ['brand', 'brand name', 'company', 'dealership', 'dealer', 'business']);

        setCsvMapping({
          name: nameCol || (headers[0] || ''),
          phone: phoneCol || (headers[1] || ''),
          email: emailCol || '',
          remarks: remarksCol || '',
          source: sourceCol || '',
          agent: agentCol || '',
          brand: brandCol || '',
        });
      },
      error: (err) => {
        setCsvError('CSV parsing error: ' + err.message);
      }
    });
  };

  const handleProcessFile = (file) => {
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      parseCsvContent(event.target.result);
    };
    reader.readAsText(file);
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleProcessFile(file);
  };

  const handleCsvPasteParse = () => {
    if (!csvPasteText.trim()) {
      setCsvError('Please paste some CSV or table data first');
      return;
    }
    parseCsvContent(csvPasteText.trim());
  };

  // Compute final mapped leads from csvRawData and csvMapping
  const preparedLeads = useMemo(() => {
    if (!csvRawData.length || !csvMapping.name || !csvMapping.phone) return [];
    return csvRawData.map((row) => {
      const name = String(row[csvMapping.name] || '').trim();
      const rawNum = String(row[csvMapping.phone] || '').trim();
      const number = sanitizePhoneClient(rawNum);
      const email = csvMapping.email ? String(row[csvMapping.email] || '').trim() : '';
      const remarks = csvMapping.remarks ? String(row[csvMapping.remarks] || '').trim() : '';
      const source = (csvMapping.source && String(row[csvMapping.source] || '').trim()) || csvDefaultSource || 'Facebook Ads';
      
      const rawAgent = csvMapping.agent ? String(row[csvMapping.agent] || '').trim() : '';
      const resolved = resolveAgent(rawAgent, employees);

      let agentId = null;
      let agentName = 'Unassigned';
      let agentStatus = 'unassigned'; // 'row_matched' | 'modal_fallback' | 'row_unmatched' | 'unassigned'

      if (resolved && resolved.id) {
        agentId = resolved.id;
        agentName = resolved.name;
        agentStatus = 'row_matched';
      } else if (resolved && resolved.unmatched) {
        if (csvTargetAgent) {
          const fallback = employees.find(e => String(e.id || e._id) === String(csvTargetAgent));
          agentId = fallback ? Number(fallback.id || fallback._id) : Number(csvTargetAgent);
          agentName = fallback ? `${fallback.name} (Default)` : 'Default Agent';
          agentStatus = 'modal_fallback';
        } else {
          agentId = null;
          agentName = `Not Found: "${rawAgent}"`;
          agentStatus = 'row_unmatched';
        }
      } else if (csvTargetAgent) {
        const fallback = employees.find(e => String(e.id || e._id) === String(csvTargetAgent));
        agentId = fallback ? Number(fallback.id || fallback._id) : Number(csvTargetAgent);
        agentName = fallback ? fallback.name : 'Default Agent';
        agentStatus = 'modal_fallback';
      }

      // Resolve Brand
      const rawBrand = csvMapping.brand ? String(row[csvMapping.brand] || '').trim() : '';
      const resolvedBrand = resolveBrand(rawBrand, brands);
      let brandId = 1;
      let brandName = 'Platinum Auto Japan';
      let brandColor = '#2563eb';

      if (resolvedBrand && resolvedBrand.id) {
        brandId = resolvedBrand.id;
        brandName = resolvedBrand.name;
        brandColor = resolvedBrand.color || '#2563eb';
      } else if (csvDefaultBrand) {
        const fallback = brands.find(b => String(b.id || b._id) === String(csvDefaultBrand));
        if (fallback) {
          brandId = Number(fallback.id || fallback._id);
          brandName = fallback.name;
          brandColor = fallback.color || '#2563eb';
        }
      }

      return {
        name,
        number,
        email,
        remarks,
        source,
        agentId,
        agentName,
        agentStatus,
        rawAgent,
        brandId,
        brandName,
        brandColor,
        rawBrand,
        isValid: Boolean(name && number)
      };
    });
  }, [csvRawData, csvMapping, csvDefaultSource, csvDefaultBrand, csvTargetAgent, employees, brands]);

  const validLeads = useMemo(() => preparedLeads.filter(l => l.isValid), [preparedLeads]);
  const invalidCount = preparedLeads.length - validLeads.length;

  const handleDownloadSampleCsv = () => {
    const sample = [
      {
        "Full Name": "John Doe",
        "Phone Number": "+14734586644",
        "Email": "john@example.com",
        "Brand": "Platinum Auto Japan",
        "Assigned Agent": "Jack Louis",
        "Source": "Facebook Ads",
        "Remarks": "Interested in Toyota Land Cruiser Prado 2022"
      },
      {
        "Full Name": "Jane Smith",
        "Phone Number": "+14734589922",
        "Email": "jane@example.com",
        "Brand": "Platinum Auto Japan",
        "Assigned Agent": "Ahsan",
        "Source": "Meta Leads Center",
        "Remarks": "Looking for Honda Vezel Hybrid"
      }
    ];
    const csvStr = Papa.unparse(sample);
    const blob = new Blob([csvStr], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = "leads_import_sample.csv";
    link.click();
  };

  const handleBulkImportSubmit = async () => {
    if (validLeads.length === 0) {
      toast.error('No valid leads found to import. Name and Phone are required.');
      return;
    }
    setCsvBusy(true);
    try {
      const payload = validLeads.map(l => ({
        name: l.name,
        number: l.number,
        email: l.email || undefined,
        source: l.source || csvDefaultSource || 'CSV Import',
        remarks: l.remarks || undefined,
        agentId: l.agentId || undefined,
        agent: l.rawAgent || undefined,
        brandId: l.brandId || (csvDefaultBrand ? Number(csvDefaultBrand) : 1),
        brand: l.brandName || undefined,
      }));
      const res = await api.post('/leads/bulk', {
        leads: payload,
        skipDuplicates,
        agentId: csvTargetAgent ? Number(csvTargetAgent) : undefined,
        brandId: csvDefaultBrand ? Number(csvDefaultBrand) : 1,
        source: csvDefaultSource || 'CSV Import',
      });

      const inserted = res.data?.inserted ?? payload.length;
      const skipped = res.data?.skippedDuplicates ?? 0;

      if (skipped > 0) {
        toast.success(`Imported ${inserted} leads (${skipped} duplicates skipped)!`, { duration: 4000 });
      } else {
        toast.success(`Successfully imported ${inserted} leads!`, { duration: 3000 });
      }

      setShowCsvModal(false);
      setCsvRawData([]);
      setCsvHeaders([]);
      setCsvFileName('');
      setCsvPasteText('');
      setCsvError('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadAll();
    } catch (err) {
      console.error('Bulk import error:', err);
      toast.error(err.response?.data?.message || 'Failed to import leads');
    } finally {
      setCsvBusy(false);
    }
  };
  const [reminderLead, setReminderLead]   = useState(null);
  const [syncingWebsite, setSyncingWebsite] = useState(false);

  // Fullscreen & Column Customizer states
  const [isFullscreen, setIsFullscreen]   = useState(false);
  const [showColModal, setShowColModal]   = useState(false);
  const [columnConfig, setColumnConfig]   = useState(getInitialColumns);

  const saveColumnConfig = (newConfig) => {
    setColumnConfig(newConfig);
    try {
      localStorage.setItem('crm_leads_columns_v1', JSON.stringify(newConfig));
    } catch (_) {}
  };

  const toggleColumnVisibility = (colId) => {
    const updated = columnConfig.map(c => c.id === colId ? { ...c, visible: !c.visible } : c);
    saveColumnConfig(updated);
  };

  const moveColumn = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= columnConfig.length) return;
    const updated = [...columnConfig];
    const [removed] = updated.splice(index, 1);
    updated.splice(targetIndex, 0, removed);
    saveColumnConfig(updated);
  };

  const resetColumnsToDefault = () => {
    saveColumnConfig(DEFAULT_COLUMN_CONFIG);
    toast.success('Columns reset to default layout');
  };

  const visibleConfigCols = useMemo(() => columnConfig.filter(c => c.visible), [columnConfig]);

  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };

  // Keyboard shortcut listener for Fullscreen (f) and Esc
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        if (e.key === 'Escape' && isFullscreen) {
          setIsFullscreen(false);
        }
        return;
      }
      if ((e.key === 'f' || e.key === 'F') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setIsFullscreen(prev => !prev);
      } else if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Density & View preferences
  const [density, setDensity]   = useState('comfortable'); // 'comfortable' | 'compact'
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'mine' | 'unassigned' | 'due' | 'cold' | 'starred'

  // Selection & Keyboard navigation
  const [selectedIds, setSelectedIds]   = useState(new Set());
  const [focusedRowIdx, setFocusedRowIdx] = useState(0);

  // Active cell popovers: { leadId, field, position }
  const [activePopover, setActivePopover] = useState(null);

  // Inline remarks editing: { leadId, text }
  const [editingRemarks, setEditingRemarks] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    agentId: '',
    status: '',
    source: '',
    startDate: '',
    endDate: '',
  });

  // Undo Stack: array of { leadIds, previousData, updatedData, description }
  const [undoStack, setUndoStack] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);
  const perPage = 30;

  // New Lead Form
  const [newLead, setNewLead] = useState({
    name: '', number: '', email: '',
    createDate: new Date().toISOString().slice(0, 10),
    source: 'Facebook Ads', status: 'New', agentId: '', brandId: '', remarks: '',
  });

  const searchInputRef = useRef(null);
  const tableRef = useRef(null);

  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const [pageInput, setPageInput] = useState('1');

  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  useEffect(() => {
    setFocusedRowIdx(0);
  }, [page]);

  const handleJumpToPage = (val) => {
    const p = parseInt(val, 10);
    if (isNaN(p)) {
      setPageInput(String(page));
      return;
    }
    const target = Math.max(1, Math.min(totalPages, p));
    if (target !== page) {
      setPage(target);
    }
    setPageInput(String(target));
  };
  const [serverTabCounts, setServerTabCounts] = useState({ all: 0, mine: 0, unassigned: 0, due: 0, cold: 0, starred: 0 });

  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 200);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Reset page to 1 whenever filters or tab changes
  useEffect(() => {
    setPage(1);
  }, [activeTab, filters.agentId, filters.status, filters.source, filters.startDate, filters.endDate, debouncedSearch]);

  // ── 1. Fetch Data ──────────────────────────────────────────
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [lr, er, br] = await Promise.all([
        api.get('/leads', { 
          params: { 
            page, 
            limit: perPage,
            tab: activeTab !== 'all' ? activeTab : undefined,
            agentId: filters.agentId || undefined,
            status: filters.status || undefined,
            source: filters.source || undefined,
            search: debouncedSearch || undefined,
            startDate: filters.startDate || undefined,
            endDate: filters.endDate || undefined,
          } 
        }),
        api.get('/employees'),
        api.get('/brands')
      ]);
      const leadsData = Array.isArray(lr.data) ? lr.data : (lr.data.leads || []);
      setLeads(leadsData);
      if (lr.data?.total != null) setTotalCount(lr.data.total);
      if (lr.data?.tabCounts) setServerTabCounts(lr.data.tabCounts);
      setEmployees(Array.isArray(er.data) ? er.data : []);
      setBrands(Array.isArray(br.data) ? br.data : []);
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, activeTab, filters.agentId, filters.status, filters.source, debouncedSearch, filters.startDate, filters.endDate]);

  const handleSyncWebsite = async () => {
    setSyncingWebsite(true);
    try {
      toast.loading("Fetching inquiries from PAJ Website...", { id: "paj-sync" });
      const { data } = await api.post("/leads/sync-website");
      if (data.success) {
        if (data.imported > 0) {
          toast.success(`Imported ${data.imported} new unassigned lead${data.imported > 1 ? 's' : ''} from website!`, { id: "paj-sync" });
          loadAll();
        } else {
          toast.success("Website leads are already up to date (no new inquiries)", { id: "paj-sync" });
        }
      } else {
        toast.error(data.error || "Could not fetch leads from PAJ Website", { id: "paj-sync" });
      }
    } catch (err) {
      toast.error("Website API currently unreachable (Check deployment)", { id: "paj-sync" });
    } finally {
      setSyncingWebsite(false);
    }
  };

  useEffect(() => { loadAll(); }, [loadAll]);

  const agents = useMemo(() => employees.filter(e => e.role === 'Agent' || e.role === 'Manager'), [employees]);
  const myId = String(user?._id || user?.id || '');

  // Map of phone number frequency for DUP badge
  const phoneCounts = useMemo(() => {
    const map = {};
    leads.forEach(l => {
      if (l.number) {
        const clean = String(l.number).trim();
        map[clean] = (map[clean] || 0) + 1;
      }
    });
    return map;
  }, [leads]);

  // ── 2. Live Tab Counts ──────────────────────────────────────
  const tabCounts = serverTabCounts;

  // ── 3. Filtered Leads ───────────────────────────────────────
  const filteredLeads = leads;
  const paginatedLeads = leads;

  // ── 4. Undo Manager ─────────────────────────────────────────
  const applyUndo = useCallback(async () => {
    if (undoStack.length === 0) return;
    const lastUndo = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));

    try {
      await Promise.all(
        lastUndo.leadIds.map(id => api.put(`/leads/${id}`, lastUndo.previousData[id]))
      );

      setLeads(prev => prev.map(l => {
        const idStr = String(l.id || l._id);
        if (lastUndo.previousData[idStr]) {
          return { ...l, ...lastUndo.previousData[idStr] };
        }
        return l;
      }));

      toast.success(`Undone: ${lastUndo.description}`);
    } catch (err) {
      toast.error('Failed to undo change');
    }
  }, [undoStack]);

  const executeUpdate = async (leadIds, patch, description) => {
    const ids = Array.isArray(leadIds) ? leadIds : [leadIds];
    const previousData = {};
    ids.forEach(id => {
      const existing = leads.find(l => String(l.id || l._id) === String(id));
      if (existing) {
        previousData[String(id)] = {};
        Object.keys(patch).forEach(k => {
          previousData[String(id)][k] = existing[k];
        });
      }
    });

    try {
      await Promise.all(ids.map(id => api.put(`/leads/${id}`, patch)));

      setLeads(prev => prev.map(l => {
        const idStr = String(l.id || l._id);
        if (ids.includes(idStr)) {
          return { ...l, ...patch };
        }
        return l;
      }));

      setUndoStack(prev => [...prev, { leadIds: ids, previousData, updatedData: patch, description }]);

      toast((t) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{description}</span>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              applyUndo();
            }}
            style={{
              background: '#38bdf8',
              color: 'var(--surface)',
              border: 'none',
              borderRadius: '4px',
              padding: '2px 8px',
              fontWeight: 700,
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            Undo
          </button>
        </span>
      ), { duration: 4000 });
    } catch (err) {
      toast.error('Failed to update lead');
    }
  };

  // Auto-scroll focused row into view
  useEffect(() => {
    const rowEl = tableRef.current?.querySelector(`tr[data-row-idx="${focusedRowIdx}"]`);
    if (rowEl) {
      rowEl.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedRowIdx]);

  // ── 5. Keyboard Shortcuts ───────────────────────────────────
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        if (e.key === 'Escape') e.target.blur();
        return;
      }

      const focusedLead = paginatedLeads[focusedRowIdx];
      const fId = focusedLead ? String(focusedLead.id || focusedLead._id) : null;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        if (focusedLead && focusedLead.number) {
          e.preventDefault();
          copyToClipboard(focusedLead.number, `Copied phone: ${focusedLead.number}`);
          return;
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        applyUndo();
        return;
      }
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      if (e.key === '?') {
        e.preventDefault();
        setShowHelpModal(true);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedRowIdx(prev => Math.min(paginatedLeads.length - 1, prev + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedRowIdx(prev => Math.max(0, prev - 1));
        return;
      }

      // Page Navigation Shortcuts: Right/Left arrows, ] / [, PageDown/PageUp
      if (e.key === 'ArrowRight' || e.key === ']' || e.key === 'PageDown') {
        e.preventDefault();
        if (e.shiftKey) {
          setPage(totalPages);
        } else {
          setPage(prev => Math.min(totalPages, prev + 1));
        }
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === '[' || e.key === 'PageUp') {
        e.preventDefault();
        if (e.shiftKey) {
          setPage(1);
        } else {
          setPage(prev => Math.max(1, prev - 1));
        }
        return;
      }

      if (!focusedLead) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        window.open(`/dashboard/leads/${fId}`, '_blank');
      } else if (e.key === ' ') {
        e.preventDefault();
        toggleSelectOne(fId);
      } else if (e.key.toLowerCase() === 's') {
        e.preventDefault();
        setActivePopover({ leadId: fId, field: 'status' });
      } else if (e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setActivePopover({ leadId: fId, field: 'agent' });
      } else if (e.key.toLowerCase() === 'c' && focusedLead.number) {
        e.preventDefault();
        window.open(`tel:${focusedLead.number}`, '_self');
      } else if (e.key.toLowerCase() === 'w' && focusedLead.number) {
        e.preventDefault();
        window.open(`https://wa.me/${focusedLead.number}`, '_blank');
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [paginatedLeads, focusedRowIdx, navigate, applyUndo, totalPages]);

  // ── 6. Selection Helpers ────────────────────────────────────
  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedLeads.length && paginatedLeads.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedLeads.map(l => String(l.id || l._id))));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── 7. Bulk Actions ─────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} leads?`)) return;
    try {
      await Promise.all(Array.from(selectedIds).map(id => api.delete(`/leads/${id}`)));
      setLeads(prev => prev.filter(l => !selectedIds.has(String(l.id || l._id))));
      setSelectedIds(new Set());
      toast.success('Leads deleted');
    } catch (err) {
      toast.error('Failed to delete leads');
    }
  };

  const handleBulkExport = () => {
    const toExport = leads.filter(l => selectedIds.has(String(l.id || l._id)));
    const csv = Papa.unparse(toExport);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `leads_selected_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    toast.success('Exported selected leads!');
  };

  const renderCell = (colId, lead, leadId, helpers) => {
    switch (colId) {
      case 'aiIntent':
        return (
          <td key="aiIntent" style={{ whiteSpace: 'nowrap' }}>
            <AIIntentBadge
              leadId={leadId}
              score={lead.intentScore}
              reason={lead.intentReason}
              compact={true}
              onScored={(score, reason) => {
                setLeads(prev => prev.map(l => String(l.id || l._id) === leadId ? { ...l, intentScore: score, intentReason: reason } : l));
              }}
            />
          </td>
        );

      case 'phone':
        return (
          <td key="phone">
            <div className="raw-phone-cell d-flex align-items-center gap-1">
              <span className={helpers.isCustomer ? "text-success fw-bold" : ""}>
                {helpers.rawPhone || '—'}
              </span>
              {helpers.isCustomer && (
                <span className="customer-pill-badge" title="Converted Customer - Do not cold dial">
                  CUSTOMER
                </span>
              )}
              {helpers.rawPhone && (
                <button
                  className="copy-hover-btn"
                  title="Copy phone"
                  onClick={() => copyToClipboard(helpers.rawPhone, `Copied phone: ${helpers.rawPhone}`)}
                >
                  <Copy size={12} />
                </button>
              )}
            </div>
          </td>
        );

      case 'email':
        return (
          <td key="email" style={{ color: 'var(--text)' }} title={lead.email}>
            {lead.email || '—'}
          </td>
        );

      case 'lastContact':
        return (
          <td key="lastContact">
            <div className="last-contact-cell">
              <span className={`staleness-dot ${helpers.dotColor}`} />
              <span>{helpers.lastContact ? timeAgo(helpers.lastContact) : 'Never'}</span>
            </div>
          </td>
        );

      case 'source':
        return (
          <td key="source">
            <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {lead.source || 'Direct'}
            </span>
          </td>
        );

      case 'status':
        return (
          <td key="status" style={{ position: 'relative' }}>
            {activePopover?.leadId === leadId && activePopover?.field === 'status' ? (
              <StatusPopover
                currentStatus={lead.status}
                options={LEAD_STATUSES}
                onSelect={(newSt) => executeUpdate(leadId, { status: newSt }, `Status updated to ${newSt}`)}
                onClose={() => setActivePopover(null)}
              />
            ) : (
              <span
                className="cell-badge"
                onClick={() => setActivePopover({ leadId, field: 'status' })}
              >
                <StatusBadge status={lead.status} />
              </span>
            )}
          </td>
        );

      case 'brand':
        return (
          <td key="brand" style={{ position: 'relative' }}>
            {activePopover?.leadId === leadId && activePopover?.field === 'brand' ? (
              <BrandPopover
                currentBrandId={lead.brandId}
                brands={brands}
                onSelect={(bId, bName) => executeUpdate(leadId, { brandId: bId, brandName: bName }, `Brand set to ${bName}`)}
                onClose={() => setActivePopover(null)}
              />
            ) : (
              <span
                className="cell-badge"
                style={{ color: helpers.brandName ? 'var(--text)' : 'var(--muted)', fontStyle: helpers.brandName ? 'normal' : 'italic' }}
                onClick={() => setActivePopover({ leadId, field: 'brand' })}
              >
                {helpers.brandName || 'Set brand'}
              </span>
            )}
          </td>
        );

      case 'agent':
        return (
          <td key="agent" style={{ position: 'relative' }}>
            {activePopover?.leadId === leadId && activePopover?.field === 'agent' ? (
              <AgentPopover
                currentAgentId={lead.agentId}
                agents={agents}
                onSelect={(aId, aName) => executeUpdate(leadId, { agentId: aId, agentName: aName }, `Agent assigned to ${aName}`)}
                onClose={() => setActivePopover(null)}
              />
            ) : (
              <div
                className="agent-cell-badge"
                onClick={() => setActivePopover({ leadId, field: 'agent' })}
              >
                <div className={`agent-avatar-small ${!lead.agentId ? 'unassigned' : ''}`}>
                  {helpers.agentInitials}
                </div>
                <span style={{ fontSize: '12.5px', color: lead.agentId ? 'var(--text)' : 'var(--muted)' }}>
                  {helpers.agentName}
                </span>
              </div>
            )}
          </td>
        );

      case 'remarks':
        return (
          <td key="remarks">
            {editingRemarks?.leadId === leadId ? (
              <input
                type="text"
                value={editingRemarks.text}
                onChange={(e) => setEditingRemarks({ leadId, text: e.target.value })}
                onBlur={() => {
                  if (editingRemarks.text !== lead.remarks) {
                    executeUpdate(leadId, { remarks: editingRemarks.text }, 'Remarks updated');
                  }
                  setEditingRemarks(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.target.blur();
                  if (e.key === 'Escape') setEditingRemarks(null);
                }}
                autoFocus
                style={{
                  background: 'var(--surface)',
                  border: '1px solid #38bdf8',
                  color: 'var(--text)',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  fontSize: '12px',
                  width: '100%',
                }}
              />
            ) : (
              <span
                style={{
                  color: lead.remarks ? 'var(--text)' : 'var(--muted)',
                  fontStyle: lead.remarks ? 'normal' : 'italic',
                  cursor: 'pointer',
                }}
                onClick={() => setEditingRemarks({ leadId, text: lead.remarks || '' })}
              >
                {lead.remarks || 'Add a note'}
              </span>
            )}
          </td>
        );

      case 'lastEdit':
        return (
          <td key="lastEdit" style={{ color: 'var(--muted)', fontSize: '11.5px', fontFamily: 'monospace' }}>
            {lead.remarksDate || lead.updatedAt ? fmtDate(lead.remarksDate || lead.updatedAt) : '—'}
          </td>
        );

      default:
        return null;
    }
  };

  // Active filter count calculation
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className={`leads-page ${isFullscreen ? 'is-fullscreen' : ''}`}>
      {/* ── Page Toolbar: Tabs & Actions ── */}
      <div className="leads-toolbar-row">
        <div className="leads-tabs" data-tour="leads-tabs">
          <button
            className={`leads-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Leads <span className="leads-tab-count">{(tabCounts.all || 0).toLocaleString()}</span>
          </button>
          <button
            className={`leads-tab-btn ${activeTab === 'mine' ? 'active' : ''}`}
            onClick={() => setActiveTab('mine')}
          >
            Assigned to me <span className="leads-tab-count">{(tabCounts.mine || 0).toLocaleString()}</span>
          </button>
          <button
            className={`leads-tab-btn ${activeTab === 'unassigned' ? 'active' : ''}`}
            onClick={() => setActiveTab('unassigned')}
          >
            Unassigned <span className="leads-tab-count">{(tabCounts.unassigned || 0).toLocaleString()}</span>
          </button>
          <button
            className={`leads-tab-btn urgent ${activeTab === 'due' ? 'active' : ''}`}
            onClick={() => setActiveTab('due')}
          >
            Due today <span className="leads-tab-count">{(tabCounts.due || 0).toLocaleString()}</span>
          </button>
          <button
            className={`leads-tab-btn ${activeTab === 'cold' ? 'active' : ''}`}
            onClick={() => setActiveTab('cold')}
          >
            Gone cold <span className="leads-tab-count">{(tabCounts.cold || 0).toLocaleString()}</span>
          </button>
          <button
            className={`leads-tab-btn ${activeTab === 'starred' ? 'active' : ''}`}
            onClick={() => setActiveTab('starred')}
          >
            Starred <span className="leads-tab-count">{(tabCounts.starred || 0).toLocaleString()}</span>
          </button>
        </div>

        <div className="leads-actions">
          <button
            className={`leads-btn outline ${isFullscreen ? 'active' : ''}`}
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit Fullscreen (Esc or F)" : "Edge-to-Edge Fullscreen (F)"}
            style={isFullscreen ? { background: 'var(--primary)', color: '#fff', borderColor: 'var(--primary)' } : {}}
          >
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            <span>{isFullscreen ? "Exit Fullscreen" : "Fullscreen"}</span>
          </button>
          <button className="leads-btn outline" onClick={() => setShowHelpModal(true)}>
            <Keyboard size={14} /> Shortcuts
          </button>
          <button 
            className="leads-btn outline" 
            onClick={handleSyncWebsite} 
            disabled={syncingWebsite}
            title="Fetch new customer inquiries & sourcing requests from PAJ Website"
          >
            <RefreshCw size={14} className={syncingWebsite ? "crm-spin" : ""} />
            <span>{syncingWebsite ? "Syncing..." : "Sync Website"}</span>
          </button>
          <button className="leads-btn outline" onClick={() => setShowCsvModal(true)}>
            <Upload size={14} /> Import CSV
          </button>
          <button className="leads-btn primary" onClick={() => setShowAddModal(true)} data-tour="add-lead-btn">
            <Plus size={14} /> Add Lead
          </button>
        </div>
      </div>

      {/* ── Filter Row ── */}
      <div className="leads-filters">
        <div className="leads-filters__left">
          <div className="leads-search-box">
            <Search size={14} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search name, phone, email, remarks..."
              value={filters.search}
              onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
            />
            <kbd>/</kbd>
          </div>

          {/* Agent Filter */}
          <select
            className="leads-filter-select"
            value={filters.agentId}
            onChange={(e) => setFilters(p => ({ ...p, agentId: e.target.value }))}
          >
            <option value="">All Agents</option>
            {agents.map(a => (
              <option key={a.id || a._id} value={a.id || a._id}>{a.name}</option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            className="leads-filter-select"
            value={filters.status}
            onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))}
          >
            <option value="">All Statuses</option>
            {LEAD_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>

          {/* Source Filter */}
          <select
            className="leads-filter-select"
            value={filters.source}
            onChange={(e) => setFilters(p => ({ ...p, source: e.target.value }))}
          >
            <option value="">All Sources</option>
            {LEAD_SOURCES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            className="leads-col-btn"
            onClick={() => setShowColModal(true)}
            title="Customize table columns & visibility"
          >
            <Columns size={13} />
            <span>Columns</span>
            <span className="leads-col-badge">
              {visibleConfigCols.length}
            </span>
          </button>

          <div className="density-toggle">
            <button
              className={`density-btn ${density === 'comfortable' ? 'active' : ''}`}
              onClick={() => setDensity('comfortable')}
            >
              Comfortable
            </button>
            <button
              className={`density-btn ${density === 'compact' ? 'active' : ''}`}
              onClick={() => setDensity('compact')}
            >
              Compact
            </button>
          </div>
        </div>
      </div>

      {/* ── Active Filter Chips Bar ── */}
      <div className="leads-chips-bar">
        {activeFilterCount === 0 && activeTab === 'all' ? (
          <span className="leads-chips-placeholder">
            No filters applied — showing all {totalCount.toLocaleString()} leads in this CRM
          </span>
        ) : (
          <span className="leads-chips-placeholder" style={{ marginRight: '10px', color: 'var(--text)' }}>
            Showing {totalCount.toLocaleString()} matching leads
          </span>
        )}
        {activeFilterCount > 0 && (
          <>
            {filters.search && (
              <span className="filter-chip">
                Search: "{filters.search}"
                <button className="filter-chip__remove" onClick={() => setFilters(p => ({ ...p, search: '' }))}>
                  <X size={12} />
                </button>
              </span>
            )}
            {filters.agentId && (
              <span className="filter-chip">
                Agent: {agents.find(a => String(a.id || a._id) === filters.agentId)?.name || 'Agent'}
                <button className="filter-chip__remove" onClick={() => setFilters(p => ({ ...p, agentId: '' }))}>
                  <X size={12} />
                </button>
              </span>
            )}
            {filters.status && (
              <span className="filter-chip">
                Status: {filters.status}
                <button className="filter-chip__remove" onClick={() => setFilters(p => ({ ...p, status: '' }))}>
                  <X size={12} />
                </button>
              </span>
            )}
            {filters.source && (
              <span className="filter-chip">
                Source: {filters.source}
                <button className="filter-chip__remove" onClick={() => setFilters(p => ({ ...p, source: '' }))}>
                  <X size={12} />
                </button>
              </span>
            )}
            <button
              className="clear-all-btn"
              onClick={() => setFilters({ search: '', agentId: '', status: '', source: '', startDate: '', endDate: '' })}
            >
              Clear all
            </button>
          </>
        )}
      </div>

      {/* ── Main Data Table Container ── */}
      <div className="leads-table-container" data-tour="leads-table">
        <table className={`leads-table ${density}`} ref={tableRef}>
          <colgroup>
            <col style={{ width: '44px', minWidth: '44px', maxWidth: '44px' }} />
            <col style={{ width: '48px', minWidth: '48px', maxWidth: '48px' }} />
            <col style={{ minWidth: '180px' }} />
          </colgroup>
          <thead>
            <tr>
              <th className="frozen-col-1" style={{ width: '44px', minWidth: '44px', maxWidth: '44px' }}>
                <input
                  type="checkbox"
                  checked={selectedIds.size > 0 && selectedIds.size === filteredLeads.length}
                  onChange={toggleSelectAll}
                />
              </th>
              <th className="frozen-col-2" style={{ width: '56px', minWidth: '56px', maxWidth: '56px' }}># ID</th>
              <th className="frozen-col-3" style={{ minWidth: '180px' }}>Name</th>
              {visibleConfigCols.map(col => (
                <th key={col.id} style={{ minWidth: col.minWidth }}>{col.label}</th>
              ))}
              <th className="pinned-actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1, 2, 3, 4, 5, 6].map(n => (
                <tr key={n} style={{ height: '44px' }}>
                  <td className="frozen-col-1"><div className="ld-skeleton-pulse" style={{ width: 16, height: 16, borderRadius: 4 }} /></td>
                  <td className="frozen-col-2"><div className="ld-skeleton-pulse" style={{ width: 18, height: 14, borderRadius: 4 }} /></td>
                  <td className="frozen-col-3"><div className="ld-skeleton-pulse" style={{ width: 120, height: 16, borderRadius: 4 }} /></td>
                  {visibleConfigCols.map(col => (
                    <td key={col.id}><div className="ld-skeleton-pulse" style={{ width: '80%', height: 16, borderRadius: 4 }} /></td>
                  ))}
                  <td className="pinned-actions-col"><div className="ld-skeleton-pulse" style={{ width: 50, height: 22, borderRadius: 6, marginLeft: 'auto' }} /></td>
                </tr>
              ))
            ) : paginatedLeads.length === 0 ? (
              <tr>
                <td colSpan={3 + visibleConfigCols.length + 1} style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
                  No leads match your current filters.
                </td>
              </tr>
            ) : (
              paginatedLeads.map((lead, idx) => {
                const leadId = String(lead.id || lead._id);
                const isSelected = selectedIds.has(leadId);
                const isFocused = idx === focusedRowIdx;
                const globalIdx = (page - 1) * perPage + idx + 1;

                // DUP badge check
                const rawPhone = String(lead.number || '').trim();
                const isDup = rawPhone && phoneCounts[rawPhone] > 1;

                // Last Contact Date & Staleness Dot
                const lastContact = lead.remarksDate || lead.updatedAt || lead.createdAt || lead.createDate;
                const dotColor = getStalenessDot(lastContact);

                // Agent details
                const agentObj = agents.find(a => String(a.id || a._id) === String(lead.agentId));
                const agentName = agentObj ? agentObj.name : (lead.agentName || 'Unassigned');
                const agentInitials = agentObj ? agentObj.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'AG';

                // Brand details
                const brandObj = brands.find(b => String(b.id || b._id) === String(lead.brandId));
                const brandName = brandObj ? brandObj.name : (lead.brandName || null);
                const isCustomer = String(lead.status || '').toLowerCase() === 'customer';

                return (
                  <tr
                    key={leadId}
                    data-row-idx={idx}
                    className={`${isSelected ? 'selected' : ''} ${isFocused ? 'focused' : ''} ${isCustomer ? 'customer-lead-row' : ''}`}
                    onClick={() => setFocusedRowIdx(idx)}
                  >
                    {/* Frozen Checkbox */}
                    <td className="frozen-col-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectOne(leadId)}
                      />
                    </td>

                    {/* Frozen Permanent Lead ID */}
                    <td className="frozen-col-2" style={{ color: isCustomer ? '#10b981' : 'var(--muted)', fontSize: '11.5px', fontFamily: 'monospace', fontWeight: 600 }} title={`Permanent Lead #${lead.id}`}>
                      #{lead.id}
                    </td>

                    {/* Frozen Name */}
                    <td className="frozen-col-3">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button
                          className={`star-btn ${lead.isFavorite ? 'starred' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            executeUpdate(leadId, { isFavorite: !lead.isFavorite }, `Favorite ${!lead.isFavorite ? 'added' : 'removed'}`);
                          }}
                        >
                          <Star size={13} fill={lead.isFavorite ? '#eab308' : 'none'} />
                        </button>
                        <span
                          style={{ fontWeight: 600, color: isCustomer ? '#10b981' : 'var(--text)', cursor: 'pointer' }}
                          onClick={() => window.open(`/dashboard/leads/${leadId}`, '_blank')}
                        >
                          {lead.name}
                        </span>
                        {isCustomer && (
                          <span className="customer-pill-badge" title="Converted Customer Account - Do not cold dial">
                            CUSTOMER
                          </span>
                        )}
                        {isDup && <span className="dup-badge">DUP</span>}
                      </div>
                    </td>

                    {/* Dynamic Configurable Columns */}
                    {visibleConfigCols.map(col => renderCell(col.id, lead, leadId, {
                      isDup, rawPhone, isCustomer, lastContact, dotColor, agentObj, agentName, agentInitials, brandObj, brandName
                    }))}

                    {/* Pinned Right Quick Actions */}
                    <td className="pinned-actions-col">
                      <div className="row-quick-actions">
                        {rawPhone && (
                          <>
                            <button
                              className="quick-act-btn"
                              title="Call lead"
                              onClick={() => window.open(`tel:${rawPhone}`, '_self')}
                            >
                              <Phone size={13} />
                            </button>
                            <button
                              className="quick-act-btn wa"
                              title="WhatsApp"
                              onClick={() => window.open(`https://wa.me/${rawPhone}`, '_blank')}
                            >
                              <MessageSquare size={13} />
                            </button>
                          </>
                        )}
                        {lead.email && (
                          <button
                            className="quick-act-btn"
                            title="Copy email"
                            onClick={() => copyToClipboard(lead.email, `Copied email: ${lead.email}`)}
                          >
                            <Copy size={13} />
                          </button>
                        )}
                        <button
                          className="quick-act-btn"
                          title="Set Reminder"
                          onClick={() => setReminderLead(lead)}
                        >
                          <Bell size={13} />
                        </button>
                        <button
                          className="quick-act-btn"
                          title="Open lead detail"
                          onClick={() => window.open(`/dashboard/leads/${leadId}`, '_blank')}
                        >
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Footer / Pagination (Centered & Clear of Chat Dock) ── */}
      <div className="leads-footer" style={{ paddingBottom: '16px', paddingRight: '110px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
          Showing <strong>{totalCount > 0 ? (page - 1) * perPage + 1 : 0}</strong>–
          <strong>{Math.min(page * perPage, totalCount)}</strong> of <strong>{totalCount.toLocaleString()}</strong> leads
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '0 auto', flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            size="sm"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage(1)}
            title="First Page (Shift + ←)"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: page <= 1 ? 'var(--muted)' : 'var(--text)',
              padding: '6px 12px',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '12px',
              height: '34px',
              cursor: page <= 1 ? 'not-allowed' : 'pointer'
            }}
          >
            « First
          </Button>

          <Button
            size="sm"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            title="Previous Page (← or [)"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: page <= 1 ? 'var(--muted)' : 'var(--text)',
              padding: '6px 14px',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '12px',
              height: '34px',
              cursor: page <= 1 ? 'not-allowed' : 'pointer'
            }}
          >
            ‹ Prev
          </Button>

          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '2px 8px',
              height: '34px'
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>Page</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleJumpToPage(pageInput);
                  e.target.blur();
                } else if (e.key === 'Escape') {
                  setPageInput(String(page));
                  e.target.blur();
                }
              }}
              onBlur={() => handleJumpToPage(pageInput)}
              onFocus={(e) => e.target.select()}
              title="Type page number and press Enter to jump"
              style={{
                width: `${Math.max(52, (String(totalPages).length + 1) * 9 + 20)}px`,
                height: '24px',
                padding: '0 4px',
                fontSize: '13px',
                fontWeight: 700,
                textAlign: 'center',
                background: 'var(--surface-1)',
                border: '1px solid var(--primary)',
                borderRadius: '5px',
                color: 'var(--text)',
                outline: 'none',
              }}
            />
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>
              of {totalPages.toLocaleString()}
            </span>
            <button
              type="button"
              onClick={() => handleJumpToPage(pageInput)}
              title="Jump to page"
              style={{
                background: 'var(--primary)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                padding: '2px 8px',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
                marginLeft: '2px',
                height: '22px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              Go
            </button>
          </div>

          <Button
            size="sm"
            variant="secondary"
            disabled={page >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            title="Next Page (→ or ])"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: page >= totalPages ? 'var(--muted)' : 'var(--text)',
              padding: '6px 14px',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '12px',
              height: '34px',
              cursor: page >= totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            Next ›
          </Button>

          <Button
            size="sm"
            variant="secondary"
            disabled={page >= totalPages}
            onClick={() => setPage(totalPages)}
            title="Last Page (Shift + →)"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: page >= totalPages ? 'var(--muted)' : 'var(--text)',
              padding: '6px 12px',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '12px',
              height: '34px',
              cursor: page >= totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            Last »
          </Button>

          <span
            className="d-none d-xl-inline"
            style={{
              fontSize: '11px',
              color: 'var(--muted)',
              marginLeft: '4px',
              padding: '3px 8px',
              borderRadius: '6px',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title="Use keyboard shortcuts to navigate pages"
          >
            <kbd style={{ fontSize: '10px', background: 'var(--surface-1)', border: '1px solid var(--border)', padding: '1px 4px', borderRadius: '3px' }}>←</kbd>
            <kbd style={{ fontSize: '10px', background: 'var(--surface-1)', border: '1px solid var(--border)', padding: '1px 4px', borderRadius: '3px' }}>→</kbd>
            <span>or</span>
            <kbd style={{ fontSize: '10px', background: 'var(--surface-1)', border: '1px solid var(--border)', padding: '1px 4px', borderRadius: '3px' }}>[</kbd>
            <kbd style={{ fontSize: '10px', background: 'var(--surface-1)', border: '1px solid var(--border)', padding: '1px 4px', borderRadius: '3px' }}>]</kbd>
          </span>
        </div>

        <div style={{ width: '100px' }} className="d-none d-lg-block" />
      </div>

      {/* ── Floating Bulk Action Bar ── */}
      <FloatingBulkBar
        selectedCount={selectedIds.size}
        onClearSelection={() => setSelectedIds(new Set())}
        onBulkAgent={(agentId, agentName) => executeUpdate(Array.from(selectedIds), { agentId, agentName }, `Assigned ${selectedIds.size} leads to ${agentName}`)}
        onBulkStatus={(status) => executeUpdate(Array.from(selectedIds), { status }, `Updated status of ${selectedIds.size} leads to ${status}`)}
        onBulkBrand={(brandId, brandName) => executeUpdate(Array.from(selectedIds), { brandId, brandName }, `Set brand of ${selectedIds.size} leads to ${brandName}`)}
        onBulkWhatsApp={() => setShowWaModal(true)}
        onBulkExport={handleBulkExport}
        onBulkDelete={handleBulkDelete}
        agents={agents}
        statuses={LEAD_STATUSES}
        brands={brands}
      />

      {/* ── Keyboard Shortcuts Modal ── */}
      <LeadHelpModal show={showHelpModal} onHide={() => setShowHelpModal(false)} />

      {/* ── Bulk WhatsApp Modal ── */}
      <BulkWhatsAppModal
        show={showWaModal}
        onHide={() => setShowWaModal(false)}
        selectedLeads={leads.filter(l => selectedIds.has(String(l.id || l._id)))}
      />

      {/* ── HubSpot-Style Customize Columns Modal ── */}
      <Modal
        show={showColModal}
        onHide={() => setShowColModal(false)}
        centered
        className="dark-theme-modal"
      >
        <Modal.Header closeButton style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', color: 'var(--text)' }}>
          <Modal.Title style={{ fontSize: '16px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Columns size={18} style={{ color: 'var(--primary)' }} /> Customize Columns
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: 'var(--surface)', color: 'var(--text)', padding: '18px' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="small text-muted">
              Toggle columns to show/hide and reorder to fit your screen.
            </span>
            <button
              className="btn btn-sm btn-link text-decoration-none p-0 d-flex align-items-center gap-1"
              style={{ color: 'var(--primary)', fontSize: '12px', fontWeight: 600 }}
              onClick={resetColumnsToDefault}
            >
              <RotateCcw size={12} /> Reset Defaults
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '380px', overflowY: 'auto', paddingRight: '4px' }}>
            {columnConfig.map((col, idx) => (
              <div
                key={col.id}
                className={`col-customizer-item ${!col.visible ? 'is-hidden' : ''}`}
              >
                <div className="d-flex align-items-center gap-2">
                  <Form.Check
                    type="checkbox"
                    id={`col-toggle-${col.id}`}
                    checked={col.visible}
                    onChange={() => toggleColumnVisibility(col.id)}
                    label={<span style={{ fontWeight: col.visible ? 600 : 400, color: col.visible ? 'var(--text)' : 'var(--muted)', fontSize: '13px' }}>{col.label}</span>}
                  />
                </div>
                <div className="d-flex align-items-center gap-1">
                  <button
                    className="btn btn-sm p-1"
                    disabled={idx === 0}
                    onClick={() => moveColumn(idx, -1)}
                    title="Move Up"
                    style={{ color: idx === 0 ? 'var(--border)' : 'var(--muted)', background: 'transparent', border: 'none' }}
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    className="btn btn-sm p-1"
                    disabled={idx === columnConfig.length - 1}
                    onClick={() => moveColumn(idx, 1)}
                    title="Move Down"
                    style={{ color: idx === columnConfig.length - 1 ? 'var(--border)' : 'var(--muted)', background: 'transparent', border: 'none' }}
                  >
                    <ArrowDown size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
          <span className="small text-muted">
            {visibleConfigCols.length} of {columnConfig.length} columns active
          </span>
          <Button variant="primary" size="sm" onClick={() => setShowColModal(false)}>
            Apply & Done
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Add Lead Modal ── */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered dialogClassName="paj-clean-modal">
        <Modal.Header>
          <Modal.Title>Create New Lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group style={{ marginBottom: '16px' }}>
            <Form.Label>Lead Name *</Form.Label>
            <Form.Control
              type="text"
              value={newLead.name}
              onChange={(e) => setNewLead(p => ({ ...p, name: e.target.value }))}
            />
          </Form.Group>
          <Form.Group style={{ marginBottom: '16px' }}>
            <Form.Label>Phone Number (Raw format) *</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g.  14734586644"
              value={newLead.number}
              onChange={(e) => setNewLead(p => ({ ...p, number: e.target.value }))}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Group>
          <Form.Group style={{ marginBottom: '16px' }}>
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={newLead.email}
              onChange={(e) => setNewLead(p => ({ ...p, email: e.target.value }))}
            />
          </Form.Group>
          <Form.Group style={{ marginBottom: '16px' }}>
            <Form.Label>Assign Agent</Form.Label>
            <Form.Select
              value={newLead.agentId}
              onChange={(e) => setNewLead(p => ({ ...p, agentId: e.target.value }))}
            >
              <option value="">Select Agent</option>
              {agents.map(a => <option key={a.id || a._id} value={a.id || a._id}>{a.name}</option>)}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={async () => {
            if (!newLead.name || !newLead.number) {
              toast.error('Name and Phone are required');
              return;
            }
            try {
              const { data } = await api.post('/leads', newLead);
              setLeads(p => [data, ...p]);
              setShowAddModal(false);
              toast.success('Lead created successfully!');
            } catch (err) {
              toast.error('Failed to create lead');
            }
          }}>Create Lead</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Bulk CSV / Leads Import Modal ── */}
      <Modal
        show={showCsvModal}
        onHide={() => {
          if (!csvBusy) setShowCsvModal(false);
        }}
        centered
        size="lg"
        className="dark-theme-modal"
      >
        <Modal.Header
          closeButton={!csvBusy}
          style={{
            background: 'var(--surface)',
            borderBottom: '1px solid var(--border)',
            color: 'var(--text)',
            padding: '16px 20px',
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <Upload size={18} style={{ color: 'var(--primary)' }} />
            <Modal.Title style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
              Import Leads (CSV & Quick Paste)
            </Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body style={{ background: 'var(--surface)', color: 'var(--text)', padding: '20px' }}>
          {/* Top Tabs: Upload CSV file vs Paste Data */}
          <div className="d-flex align-items-center justify-content-between mb-3 pb-2" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="d-flex gap-2">
              <Button
                variant={csvTab === 'file' ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setCsvTab('file')}
                style={{ fontSize: '13px', fontWeight: 500 }}
              >
                Upload File (.csv, .tsv, .txt)
              </Button>
              <Button
                variant={csvTab === 'paste' ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setCsvTab('paste')}
                style={{ fontSize: '13px', fontWeight: 500 }}
              >
                Quick Paste Table / Text
              </Button>
            </div>

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleDownloadSampleCsv}
              className="d-flex align-items-center gap-1"
              style={{ fontSize: '12px', border: '1px solid var(--border)' }}
            >
              <Download size={13} />
              <span>Sample CSV Template</span>
            </Button>
          </div>

          {/* Mode 1: File Upload with Drag & Drop */}
          {csvTab === 'file' && (
            <div
              className={`p-4 mb-3 text-center`}
              style={{
                border: isDragging ? '2px dashed var(--primary)' : '2px dashed var(--border)',
                borderRadius: '8px',
                background: isDragging ? 'rgba(59, 130, 246, 0.08)' : 'var(--surface-2)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
                const file = e.dataTransfer?.files?.[0];
                if (file) handleProcessFile(file);
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv,.tsv,text/tab-separated-values,.txt,text/plain"
                style={{ display: 'none' }}
                onChange={handleCsvFileChange}
              />
              <FileSpreadsheet size={34} style={{ color: isDragging ? 'var(--primary)' : 'var(--muted)', marginBottom: '8px' }} className="mx-auto" />
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                {csvFileName ? csvFileName : "Click or drag & drop CSV file here"}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                Supports standard exports from Meta Leads Center, Google Sheets, or Excel
              </div>
            </div>
          )}

          {/* Mode 2: Quick Paste Area */}
          {csvTab === 'paste' && (
            <div className="mb-3">
              <Form.Label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)' }}>
                Paste CSV or Tab-Separated Data (Directly from Excel, Sheets, or WhatsApp)
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder={"Name\tPhone\tEmail\tBrand\tAssigned Agent\tSource\tRemarks\nJohn Doe\t+14734586644\tjohn@example.com\tPlatinum Auto Japan\tJack Louis\tFacebook Ads\tInterested in Prado"}
                value={csvPasteText}
                onChange={(e) => setCsvPasteText(e.target.value)}
                style={{
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                  fontSize: '13px',
                  fontFamily: 'monospace',
                }}
              />
              <div className="d-flex justify-content-end mt-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleCsvPasteParse}
                  style={{ fontSize: '12px' }}
                >
                  Parse Pasted Data
                </Button>
              </div>
            </div>
          )}

          {/* Error display */}
          {csvError && (
            <div
              className="p-2 mb-3 d-flex align-items-center gap-2"
              style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '6px',
                color: '#ef4444',
                fontSize: '12px',
              }}
            >
              <AlertCircle size={15} />
              <span>{csvError}</span>
            </div>
          )}

          {/* Configuration & Column Mapping (visible if rows parsed) */}
          {csvHeaders.length > 0 && (
            <div
              className="p-3 mb-3"
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
              }}
            >
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: 'var(--text)' }}>
                Field Mapping (CSV Columns)
              </div>

              <div className="row g-2 mb-3">
                <div className="col-12 col-md-4">
                  <Form.Label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>
                    Name Column *
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={csvMapping.name}
                    onChange={(e) => setCsvMapping(p => ({ ...p, name: e.target.value }))}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: '13px' }}
                  >
                    <option value="">Select column...</option>
                    {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                  </Form.Select>
                </div>

                <div className="col-12 col-md-4">
                  <Form.Label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>
                    Phone Column *
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={csvMapping.phone}
                    onChange={(e) => setCsvMapping(p => ({ ...p, phone: e.target.value }))}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: '13px' }}
                  >
                    <option value="">Select column...</option>
                    {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                  </Form.Select>
                </div>

                <div className="col-12 col-md-4">
                  <Form.Label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>
                    Email Column
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={csvMapping.email}
                    onChange={(e) => setCsvMapping(p => ({ ...p, email: e.target.value }))}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: '13px' }}
                  >
                    <option value="">None / Optional</option>
                    {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                  </Form.Select>
                </div>

                <div className="col-12 col-md-4">
                  <Form.Label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>
                    Brand Column
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={csvMapping.brand}
                    onChange={(e) => setCsvMapping(p => ({ ...p, brand: e.target.value }))}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: '13px' }}
                  >
                    <option value="">None (Use Default Brand)</option>
                    {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                  </Form.Select>
                </div>

                <div className="col-12 col-md-4">
                  <Form.Label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>
                    Assigned Agent Column
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={csvMapping.agent}
                    onChange={(e) => setCsvMapping(p => ({ ...p, agent: e.target.value }))}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: '13px' }}
                  >
                    <option value="">None (Use Fallback Agent)</option>
                    {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                  </Form.Select>
                </div>

                <div className="col-12 col-md-4">
                  <Form.Label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>
                    Source Column
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={csvMapping.source}
                    onChange={(e) => setCsvMapping(p => ({ ...p, source: e.target.value }))}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: '13px' }}
                  >
                    <option value="">None (Use Default Source)</option>
                    {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                  </Form.Select>
                </div>

                <div className="col-12 col-md-4">
                  <Form.Label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>
                    Remarks / Notes Column
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={csvMapping.remarks}
                    onChange={(e) => setCsvMapping(p => ({ ...p, remarks: e.target.value }))}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: '13px' }}
                  >
                    <option value="">None / Optional</option>
                    {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                  </Form.Select>
                </div>
              </div>

              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: 'var(--text)', borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
                Batch Defaults & Fallbacks
              </div>

              <div className="row g-2 mb-3">
                <div className="col-12 col-md-4">
                  <Form.Label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>
                    Default Brand
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={csvDefaultBrand}
                    onChange={(e) => setCsvDefaultBrand(e.target.value)}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: '13px' }}
                  >
                    {brands.length > 0 ? (
                      brands.map(b => (
                        <option key={b.id || b._id} value={b.id || b._id}>
                          {b.name}
                        </option>
                      ))
                    ) : (
                      <option value="1">Platinum Auto Japan</option>
                    )}
                  </Form.Select>
                  <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
                    Used if CSV has no brand column
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <Form.Label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>
                    Fallback / Default Agent
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={csvTargetAgent}
                    onChange={(e) => setCsvTargetAgent(e.target.value)}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: '13px' }}
                  >
                    <option value="">Unassigned (Open Pool)</option>
                    {agents.map(a => <option key={a.id || a._id} value={a.id || a._id}>{a.name}</option>)}
                  </Form.Select>
                  <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
                    Used if CSV has no agent column or blank
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <Form.Label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)' }}>
                    Default Lead Source
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={csvDefaultSource}
                    onChange={(e) => setCsvDefaultSource(e.target.value)}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: '13px' }}
                  >
                    <option value="Facebook Ads">Facebook Ads</option>
                    <option value="Meta Leads Center">Meta Leads Center</option>
                    <option value="CSV Import">CSV Import</option>
                    <option value="Whatsapp Ads">Whatsapp Ads</option>
                    <option value="Insta Ads">Insta Ads</option>
                    <option value="Website">Website</option>
                    <option value="Referral">Referral</option>
                    <option value="Walk-in">Walk-in</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                  <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px' }}>
                    Used if CSV has no source column or row source is blank
                  </div>
                </div>
              </div>

              {/* Deduplication Switch */}
              <div className="d-flex align-items-center justify-content-between p-2 mb-2" style={{ background: 'var(--surface)', borderRadius: '6px', border: '1px solid var(--border)' }}>
                <Form.Check
                  type="switch"
                  id="skip-duplicates-toggle"
                  label="Skip duplicates (automatically ignore phone numbers already in CRM or repeated in this batch)"
                  checked={skipDuplicates}
                  onChange={(e) => setSkipDuplicates(e.target.checked)}
                  style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text)' }}
                />
              </div>

              {/* Status pill counts */}
              <div className="d-flex align-items-center gap-3 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: '12px' }}>
                  <span className="text-muted">Total parsed: </span>
                  <span className="fw-semibold text-light">{csvRawData.length}</span>
                </div>
                <div style={{ fontSize: '12px' }}>
                  <span className="text-success">Valid leads: </span>
                  <span className="fw-semibold text-success">{validLeads.length}</span>
                </div>
                {invalidCount > 0 && (
                  <div style={{ fontSize: '12px' }}>
                    <span className="text-warning">Skipped (missing name/phone): </span>
                    <span className="fw-semibold text-warning">{invalidCount}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Live Preview Table */}
          {validLeads.length > 0 && (
            <div className="mb-2">
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--muted)', marginBottom: '6px' }}>
                Preview (First 5 Rows):
              </div>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '6px' }}>
                <table className="table table-dark table-sm table-striped mb-0" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-3)', color: 'var(--muted)' }}>
                      <th>#</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Brand</th>
                      <th>Assigned Agent</th>
                      <th>Source</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validLeads.slice(0, 5).map((lead, idx) => (
                      <tr key={idx}>
                        <td className="text-muted">{idx + 1}</td>
                        <td className="fw-semibold">{lead.name}</td>
                        <td className="font-monospace text-info">{lead.number}</td>
                        <td className="text-muted">{lead.email || '—'}</td>
                        <td>
                          <span className="badge" style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)', fontWeight: 600, fontSize: '11px' }}>
                            {lead.brandName}
                          </span>
                        </td>
                        <td>
                          {lead.agentStatus === 'row_matched' && (
                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
                              {lead.agentName}
                            </span>
                          )}
                          {lead.agentStatus === 'modal_fallback' && (
                            <span className="badge bg-info-subtle text-info border border-info-subtle">
                              {lead.agentName}
                            </span>
                          )}
                          {lead.agentStatus === 'row_unmatched' && (
                            <span className="badge bg-warning-subtle text-warning border border-warning-subtle" title="Agent name not matched to system user">
                              {lead.agentName}
                            </span>
                          )}
                          {lead.agentStatus === 'unassigned' && (
                            <span className="text-muted small">Unassigned</span>
                          )}
                        </td>
                        <td><span className="badge bg-secondary">{lead.source}</span></td>
                        <td className="text-truncate" style={{ maxWidth: '150px' }}>{lead.remarks || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', padding: '12px 20px' }}>
          <Button
            variant="secondary"
            size="sm"
            disabled={csvBusy}
            onClick={() => setShowCsvModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={csvBusy || validLeads.length === 0}
            onClick={handleBulkImportSubmit}
            className="d-flex align-items-center gap-2"
          >
            <Upload size={14} />
            <span>
              {csvBusy ? 'Importing...' : `Import ${validLeads.length} Lead${validLeads.length === 1 ? '' : 's'}`}
            </span>
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Set Reminder Modal */}
      <SetReminderModal
        show={Boolean(reminderLead)}
        onClose={() => setReminderLead(null)}
        lead={reminderLead}
      />
    </div>
  );
};

export default Leads;